import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FIELD_COLORS = {
  pest365: '#2563eb',
  pest365Plus: '#8b5cf6',
  oneTime: '#f59e0b',
  initialServiceVisit: '#16a34a',
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One Time',
  initialServiceVisit: 'Initial Service Visit',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: '4px 0 20px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: 14,
  },
  gaugeContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 24,
  },
  gaugeLabel: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 600,
  },
  gaugeValue: {
    fontSize: 36,
    fontWeight: 700,
    marginTop: 4,
  },
  gaugeInterpretation: {
    fontSize: 13,
    color: '#6b7280',
    marginTop: 2,
  },
  chartContainer: {
    marginBottom: 24,
  },
  legendRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 16,
    marginTop: 8,
    flexWrap: 'wrap',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#374151',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  cardsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
  },
  fieldCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 14,
    textAlign: 'center',
  },
  fieldCardLabel: {
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 6,
  },
  fieldCardValue: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 4,
  },
  fieldCardInterpretation: {
    fontSize: 12,
    color: '#6b7280',
    fontStyle: 'italic',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: 'rgba(17, 24, 39, 0.92)',
    color: '#fff',
    borderRadius: 6,
    padding: '8px 12px',
    fontSize: 12,
    lineHeight: 1.5,
    zIndex: 9999,
    maxWidth: 220,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  tooltipDate: {
    fontWeight: 600,
    marginBottom: 4,
    borderBottom: '1px solid rgba(255,255,255,0.2)',
    paddingBottom: 4,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  tooltipDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
};

function getComplexityColor(value) {
  if (value <= 0.3) return '#16a34a';
  if (value <= 0.6) return '#f59e0b';
  return '#ef4444';
}

function getOverallInterpretation(value) {
  if (value <= 0.15) return 'Highly predictable';
  if (value <= 0.3) return 'Predictable';
  if (value <= 0.45) return 'Moderately predictable';
  if (value <= 0.6) return 'Moderately complex';
  if (value <= 0.8) return 'Complex';
  return 'Highly chaotic';
}

export default function PriceComplexityIndex({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const fieldEntries = useMemo(() => {
    if (!data || !data.fields) return [];
    return Object.entries(data.fields).filter(
      ([key]) => FIELD_COLORS[key]
    );
  }, [data]);

  const allSeries = useMemo(() => {
    return fieldEntries.map(([key, field]) => ({
      key,
      color: FIELD_COLORS[key],
      label: FIELD_LABELS[key],
      points: (field.dates || []).map((d, i) => ({
        date: new Date(d),
        value: field.complexity[i],
      })),
    }));
  }, [fieldEntries]);

  useEffect(() => {
    if (!svgRef.current || allSeries.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 250;
    const margin = { top: 20, right: 20, bottom: 30, left: 45 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const allDates = allSeries.flatMap((s) => s.points.map((p) => p.date));
    const xExtent = d3.extent(allDates);

    const xScale = d3.scaleTime().domain(xExtent).range([0, innerW]);
    const yScale = d3.scaleLinear().domain([0, 1]).range([innerH, 0]);

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // Reference zones
    const zones = [
      { y0: 0, y1: 0.3, color: '#16a34a', opacity: 0.06, label: 'Predictable' },
      { y0: 0.3, y1: 0.6, color: '#f59e0b', opacity: 0.06, label: 'Moderate' },
      { y0: 0.6, y1: 1.0, color: '#ef4444', opacity: 0.06, label: 'Complex' },
    ];

    zones.forEach((zone) => {
      g.append('rect')
        .attr('x', 0)
        .attr('y', yScale(zone.y1))
        .attr('width', innerW)
        .attr('height', yScale(zone.y0) - yScale(zone.y1))
        .attr('fill', zone.color)
        .attr('opacity', zone.opacity);

      g.append('text')
        .attr('x', innerW - 4)
        .attr('y', yScale((zone.y0 + zone.y1) / 2))
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', 10)
        .attr('fill', zone.color)
        .attr('opacity', 0.7)
        .text(zone.label);
    });

    // Zone boundary lines
    [0.3, 0.6].forEach((val) => {
      g.append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', yScale(val))
        .attr('y2', yScale(val))
        .attr('stroke', '#d1d5db')
        .attr('stroke-dasharray', '4,3')
        .attr('stroke-width', 1);
    });

    // Axes
    const xAxis = d3.axisBottom(xScale).ticks(6).tickFormat(d3.timeFormat('%b %d'));
    const yAxis = d3.axisLeft(yScale).ticks(5).tickFormat(d3.format('.1f'));

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(xAxis)
      .selectAll('text')
      .attr('font-size', 10)
      .attr('fill', '#6b7280');

    g.append('g')
      .call(yAxis)
      .selectAll('text')
      .attr('font-size', 10)
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#d1d5db');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Lines
    const lineGen = d3.line()
      .x((d) => xScale(d.date))
      .y((d) => yScale(d.value))
      .curve(d3.curveMonotoneX);

    allSeries.forEach((series) => {
      if (series.points.length === 0) return;

      g.append('path')
        .datum(series.points)
        .attr('fill', 'none')
        .attr('stroke', series.color)
        .attr('stroke-width', 2)
        .attr('d', lineGen);
    });

    // Invisible overlay for hover
    const bisect = d3.bisector((d) => d.date).left;

    const overlay = g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair');

    const hoverLine = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    const hoverDots = allSeries.map((series) =>
      g.append('circle')
        .attr('r', 4)
        .attr('fill', series.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('display', 'none')
    );

    overlay.on('mousemove', function (event) {
      const [mx] = d3.pointer(event, this);
      const hoveredDate = xScale.invert(mx);

      const values = allSeries.map((series) => {
        if (series.points.length === 0) return { key: series.key, label: series.label, color: series.color, value: null };
        const idx = bisect(series.points, hoveredDate, 1);
        const d0 = series.points[idx - 1];
        const d1 = series.points[idx];
        let closest;
        if (!d1) {
          closest = d0;
        } else if (!d0) {
          closest = d1;
        } else {
          closest = hoveredDate - d0.date > d1.date - hoveredDate ? d1 : d0;
        }
        return { key: series.key, label: series.label, color: series.color, value: closest.value, date: closest.date };
      });

      const validValues = values.filter((v) => v.value !== null);
      if (validValues.length === 0) return;

      const refDate = validValues[0].date;
      const xPos = xScale(refDate);

      hoverLine
        .attr('x1', xPos)
        .attr('x2', xPos)
        .style('display', null);

      allSeries.forEach((series, i) => {
        const match = values.find((v) => v.key === series.key);
        if (match && match.value !== null) {
          hoverDots[i]
            .attr('cx', xPos)
            .attr('cy', yScale(match.value))
            .style('display', null);
        } else {
          hoverDots[i].style('display', 'none');
        }
      });

      setTooltip({
        x: event.clientX + 14,
        y: event.clientY - 10,
        date: d3.timeFormat('%Y-%m-%d')(refDate),
        values: validValues,
      });
    });

    overlay.on('mouseleave', function () {
      hoverLine.style('display', 'none');
      hoverDots.forEach((dot) => dot.style('display', 'none'));
      setTooltip(null);
    });
  }, [allSeries]);

  if (!data || !data.fields || Object.keys(data.fields).length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Complexity Index</h3>
        <p style={styles.subtitle}>Measuring predictability of pricing behavior</p>
        <div style={styles.emptyState}>No complexity data available.</div>
      </div>
    );
  }

  const overallValue = typeof data.overall === 'number' ? data.overall : 0;
  const overallColor = getComplexityColor(overallValue);

  // Gauge arc parameters
  const gaugeRadius = 70;
  const gaugeThickness = 14;
  const gaugeCx = 90;
  const gaugeCy = 80;
  const startAngle = -Math.PI * 0.75;
  const endAngle = Math.PI * 0.75;
  const totalAngle = endAngle - startAngle;

  const arcBg = d3.arc()
    .innerRadius(gaugeRadius - gaugeThickness)
    .outerRadius(gaugeRadius)
    .startAngle(startAngle)
    .endAngle(endAngle)
    .cornerRadius(gaugeThickness / 2);

  const arcFg = d3.arc()
    .innerRadius(gaugeRadius - gaugeThickness)
    .outerRadius(gaugeRadius)
    .startAngle(startAngle)
    .endAngle(startAngle + totalAngle * Math.min(overallValue, 1))
    .cornerRadius(gaugeThickness / 2);

  // Gradient stops for gauge background
  const gaugeGradientStops = [
    { offset: '0%', color: '#16a34a' },
    { offset: '30%', color: '#16a34a' },
    { offset: '50%', color: '#f59e0b' },
    { offset: '70%', color: '#f59e0b' },
    { offset: '100%', color: '#ef4444' },
  ];

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Complexity Index</h3>
      <p style={styles.subtitle}>Measuring predictability of pricing behavior</p>

      {/* Gauge */}
      <div style={styles.gaugeContainer}>
        <svg width={180} height={110} viewBox="0 0 180 110">
          <defs>
            <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              {gaugeGradientStops.map((s, i) => (
                <stop key={i} offset={s.offset} stopColor={s.color} />
              ))}
            </linearGradient>
          </defs>
          <g transform={`translate(${gaugeCx},${gaugeCy})`}>
            <path d={arcBg()} fill="#f3f4f6" />
            <path d={arcFg()} fill={overallColor} />
          </g>
          <text
            x={gaugeCx}
            y={gaugeCy + 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="24"
            fontWeight="700"
            fill={overallColor}
          >
            {overallValue.toFixed(2)}
          </text>
          {/* Scale labels */}
          <text x={18} y={105} fontSize="9" fill="#9ca3af" textAnchor="middle">0</text>
          <text x={gaugeCx} y={12} fontSize="9" fill="#9ca3af" textAnchor="middle">0.5</text>
          <text x={162} y={105} fontSize="9" fill="#9ca3af" textAnchor="middle">1.0</text>
        </svg>
        <div style={styles.gaugeLabel}>Overall Complexity</div>
        <div style={styles.gaugeInterpretation}>{getOverallInterpretation(overallValue)}</div>
      </div>

      {/* Multi-line chart */}
      <div style={styles.chartContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 250"
          width="100%"
          style={{ display: 'block' }}
        />
        <div style={styles.legendRow}>
          {fieldEntries.map(([key]) => (
            <div key={key} style={styles.legendItem}>
              <span style={{ ...styles.legendDot, background: FIELD_COLORS[key] }} />
              {FIELD_LABELS[key]}
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={styles.tooltipDate}>{tooltip.date}</div>
          {tooltip.values.map((v) => (
            <div key={v.key} style={styles.tooltipRow}>
              <span>
                <span style={{ ...styles.tooltipDot, background: v.color }} />
                {v.label}
              </span>
              <span>{v.value.toFixed(3)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Per-field summary cards */}
      <div style={styles.cardsRow}>
        {fieldEntries.map(([key, field]) => {
          const currentValue = field.complexity && field.complexity.length > 0
            ? field.complexity[field.complexity.length - 1]
            : null;
          return (
            <div key={key} style={{ ...styles.fieldCard, borderTop: `3px solid ${FIELD_COLORS[key]}` }}>
              <div style={styles.fieldCardLabel}>{FIELD_LABELS[key]}</div>
              <div style={{ ...styles.fieldCardValue, color: currentValue !== null ? getComplexityColor(currentValue) : '#9ca3af' }}>
                {currentValue !== null ? currentValue.toFixed(2) : '--'}
              </div>
              <div style={styles.fieldCardInterpretation}>
                {field.interpretation || '--'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
